import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Topbar from '../PulseSurvey/Topbar';
import { successToast, errorToast } from '../../utils/helper';
import { Api } from '../../api';
import {
  createCustomOffering,
  updateCustomOffering,
  getCustomOfferingById
} from '../../api/offeringApi';

const CreateCustomOffering = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const perkId = searchParams.get('id');
  const isEditMode = !!perkId;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    companyIds: [], // For multi-select companies
    isB2C: false, // Flag for B2C option
    title: '',
    image: null,
    website: '',
    phone: '',
    description: '',
    other: '',
    status: 'draft'
  });

  // Company selection states
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]); // Changed to array for multi-select
  const [companySearchText, setCompanySearchText] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);
  const [companyHasMore, setCompanyHasMore] = useState(true);
  const companyDropdownRef = useRef(null);

  const fetchInfo = () => {
    console.log('fetchInfo function in Create Custom Perk page');
  };

  // Load perk data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadPerkData();
    } else {
      setPageLoading(false);
    }
  }, [perkId]);

  const loadPerkData = async () => {
    try {
      const response = await getCustomOfferingById(perkId);
      if (response?.data) {
        const perk = response.data;

        // Handle B2C or multiple company IDs from backend
        const isB2C = perk.isB2C || false;
        const companyIds = perk.companyIds || (perk.companyId ? [perk.companyId.id] : []);

        setFormData({
          id: perk.id,
          companyId: perk.companyId?.id || '',
          companyIds: companyIds,
          isB2C: isB2C,
          title: perk.title,
          image: perk.image || null,
          website: perk.website || '',
          phone: perk.phone || '',
          description: perk.description || '',
          other: perk.other || '',
          status: perk.status || 'approved'
        });
        // Set image preview from existing image URL
        if (perk.image) {
          setImagePreview(perk.image);
        }
        // Set company search text to trigger fetching the company
        if (perk.companyId?.companyName) {
          setCompanySearchText(perk.companyId.companyName);
        }
        setPageLoading(false);
      }
    } catch (error) {
      console.error('Error loading perk:', error);
      errorToast('Failed to load perk data');
      setPageLoading(false);
    }
  };

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies(1, '', false);
  }, []);

  // Find and set selected companies when companies are loaded
  useEffect(() => {
    if (companies.length > 0 && formData.companyIds.length > 0) {
      const foundCompanies = companies.filter((company) =>
        formData.companyIds.includes(company._id)
      );
      setSelectedCompanies(foundCompanies);
    }
  }, [companies, formData.companyIds]);

  // Handle company search with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isCompanyDropdownOpen) {
        fetchCompanies(1, companySearchText, false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companySearchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCompanies = async (page = 1, searchKey = '', isAppending = false) => {
    if (companyLoading) return;

    setCompanyLoading(true);
    try {
      const response = await Api.getAllCompanies(page, 10, searchKey, 'companyName', 1);

      if (response?.data?.meta?.code === 1) {
        const newCompanies = response.data.data || [];
        const totalCount = response.data.meta?.totalRecords || 0;

        if (isAppending) {
          setCompanies((prev) => [...prev, ...newCompanies]);
        } else {
          setCompanies(newCompanies);
        }

        setCompanyCurrentPage(page);
        setCompanyHasMore(
          newCompanies.length === 0 ? false : companies.length + newCompanies.length < totalCount
        );
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      errorToast('Failed to load companies');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCompanySelect = (company) => {
    if (company._id === 'b2c') {
      // Toggle B2C selection without clearing companies
      const newIsB2C = !formData.isB2C;
      setFormData((prev) => ({
        ...prev,
        isB2C: newIsB2C
      }));
    } else {
      // Toggle company in multi-select without affecting B2C
      const isSelected = selectedCompanies.some((c) => c._id === company._id);

      let newSelectedCompanies;
      if (isSelected) {
        newSelectedCompanies = selectedCompanies.filter((c) => c._id !== company._id);
      } else {
        newSelectedCompanies = [...selectedCompanies, company];
      }

      setSelectedCompanies(newSelectedCompanies);
      setFormData((prev) => ({
        ...prev,
        companyIds: newSelectedCompanies.map((c) => c._id),
        companyId: newSelectedCompanies.length > 0 ? newSelectedCompanies[0]._id : ''
      }));
    }
  };

  const handleCompanySearch = (e) => {
    setCompanySearchText(e.target.value);
    setCompanyCurrentPage(1);
  };

  const handleCompanyScroll = useCallback(
    (e) => {
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      if (bottom && companyHasMore && !companyLoading) {
        fetchCompanies(companyCurrentPage + 1, companySearchText, true);
      }
    },
    [companyHasMore, companyLoading, companyCurrentPage, companySearchText]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null
    }));
    setImagePreview(null);
  };

  const validateForm = () => {
    if (!formData.isB2C && formData.companyIds.length === 0) {
      errorToast('Please select at least one company or B2C option');
      return false;
    }
    if (!formData.title.trim()) {
      errorToast('Title is required');
      return false;
    }
    if (!formData.description.trim()) {
      errorToast('Description is required');
      return false;
    }
    return true;
  };

  const prepareFormData = () => {
    const payload = new FormData();
    if (formData.id) {
      payload.append('id', formData.id);
    } else {
      if (formData.companyIds?.length > 0) {
        formData.companyIds.forEach((companyId) => {
          payload.append('companyIds[]', companyId);
        });
        // Also keep backward compatibility with single companyId
        payload.append('companyId', formData.companyIds[0]);
      }
    }

    // Add B2C flag if selected
    if (formData.isB2C) {
      payload.append('isB2C', 'true');
    }

    // Add company IDs if any are selected

    payload.append('title', formData.title);
    payload.append('description', formData.description);
    payload.append('website', formData.website);
    payload.append('phone', formData.phone);
    payload.append('other', formData.other);
    payload.append('status', formData.status);

    // Add image if it's a new file (not a string URL from previous upload)
    if (formData.image instanceof File) {
      payload.append('image', formData.image);
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = prepareFormData();
      let response;

      if (isEditMode) {
        response = await updateCustomOffering(perkId, payload);
      } else {
        response = await createCustomOffering(payload);
      }

      if (response?.meta?.code === 1 || response?.status === 200) {
        successToast(
          isEditMode ? 'Custom perk updated successfully!' : 'Custom perk created successfully!'
        );
        // Navigate back to perks page
        setTimeout(() => {
          navigate('/offering');
        }, 1500);
      } else {
        errorToast(response?.message || 'Failed to save perk');
      }
    } catch (error) {
      console.error('Error saving perk:', error);
      errorToast(error.message || 'Failed to save custom perk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>{isEditMode ? 'Edit Custom Perk' : 'Create Custom Perk'} | Shoorah Admin</title>
        </Helmet>
        <Topbar
          title={isEditMode ? 'Edit Custom Perk' : 'Create Custom Perk'}
          infoBtn={false}
          setOpen={setOpen}
          fetchInfo={fetchInfo}
        />

        {/* Back Button */}
        <div className="mt-6 mb-6">
          <button
            onClick={() => navigate('/offering')}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Back to Perks</span>
          </button>
        </div>

        {/* Loading State */}
        {pageLoading && (
          <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6 flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading perk...</p>
            </div>
          </div>
        )}

        {/* Form Container */}
        {!pageLoading && (
          <div className="w-full  bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
            {/* Company Selection Section */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-shoorah-darkBorder">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      Select Company
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Choose a company for this perk
                    </p>
                  </div>

                  {/* Company Dropdown */}
                  <div className="relative w-full sm:w-96" ref={companyDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                      disabled={isEditMode}
                      className={`w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors ${
                        isEditMode ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-blue-600 dark:text-blue-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          {formData.isB2C && selectedCompanies.length > 0 ? (
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                B2C + {selectedCompanies.length}{' '}
                                {selectedCompanies.length === 1 ? 'Company' : 'Companies'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {selectedCompanies.map((c) => c.company_name).join(', ')}
                              </p>
                            </div>
                          ) : formData.isB2C ? (
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              B2C (Direct Users)
                            </p>
                          ) : selectedCompanies.length > 0 ? (
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {selectedCompanies.length}{' '}
                                {selectedCompanies.length === 1 ? 'Company' : 'Companies'} Selected
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {selectedCompanies.map((c) => c.company_name).join(', ')}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Select companies or B2C...
                            </p>
                          )}
                        </div>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          isCompanyDropdownOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {isCompanyDropdownOpen && !isEditMode && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
                        {/* Search Input */}
                        <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                          <input
                            type="text"
                            placeholder="Search companies..."
                            value={companySearchText}
                            onChange={handleCompanySearch}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>

                        {/* Company List */}
                        <div className="overflow-y-auto flex-1" onScroll={handleCompanyScroll}>
                          {/* B2C Option at the top */}
                          <button
                            type="button"
                            onClick={() =>
                              handleCompanySelect({
                                _id: 'b2c',
                                company_name: 'B2C (Direct Users)'
                              })
                            }
                            className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-left border-b border-gray-200 dark:border-shoorah-darkBorder ${
                              formData.isB2C ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={formData.isB2C}
                                onChange={() => {}}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                            </div>
                            <div className="flex-shrink-0 w-8 h-8  rounded-full flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-purple-600 dark:text-purple-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                B2C (Direct Users)
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Users not associated with any company
                              </p>
                            </div>
                          </button>

                          {companyLoading && companies.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                              Loading companies...
                            </div>
                          ) : companies.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                              No companies found
                            </div>
                          ) : (
                            <>
                              {companies.map((company) => {
                                const isSelected = selectedCompanies.some(
                                  (c) => c._id === company._id
                                );
                                return (
                                  <button
                                    key={company._id}
                                    type="button"
                                    onClick={() => handleCompanySelect(company)}
                                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
                                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                                  >
                                    <div className="flex-shrink-0">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                      />
                                    </div>
                                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                      {company.company_logo ? (
                                        <img
                                          src={company.company_logo}
                                          alt={company.company_name}
                                          className="w-8 h-8 rounded-full object-cover"
                                        />
                                      ) : (
                                        <svg
                                          className="w-4 h-4 text-gray-600 dark:text-gray-300"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {company.company_name}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {company.company_email || 'No email'}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                              {companyLoading && (
                                <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                                  Loading more...
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter perk title"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Image Upload Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Image
                </label>
                <div className="flex flex-col space-y-3">
                  {imagePreview ? (
                    <div className="relative w-[300px] h-[300px] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full max-h-[200px] object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 p-2 rounded-full text-red-600 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 w-[300px] h-[300px] flex justify-center items-center border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        name="image"
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="cursor-pointer flex flex-col items-center justify-center"
                      >
                        <svg
                          className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Click to upload image
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Website Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter detailed description of the perk"
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* Other Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Other Information
                </label>
                <textarea
                  name="other"
                  value={formData.other}
                  onChange={handleInputChange}
                  placeholder="Any additional information"
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* Status Field */}
              {isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3  pt-4 border-t border-gray-200 pt-5 dark:border-shoorah-darkBorder">
                <div className=" w-[50%]"></div>
                <button
                  type="button"
                  onClick={() => navigate('/offering')}
                  className="flex-1 px-6 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-2.5 rounded-lg bg-blue-600 dark:bg-blue-700 text-white font-medium hover:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Perk'
                    : 'Create Perk'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateCustomOffering;
